import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { toCSV } from "@/components/DataTable";
import ExportPreview from "@/components/ExportPreview";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Printer } from "lucide-react";

const COLS = [
  ["employee_nama", "Nama", 0], ["hari_masuk", "Hari", 1], ["jam_lembur", "Lembur", 1],
  ["gaji_per_hari", "Gaji/Hari", 2], ["gaji_pokok", "Gaji", 2], ["uang_makan", "U.Makan", 2],
  ["uang_luar_kota", "U.Luar Kota", 2], ["uang_lembur", "U.Lembur", 2], ["total_tunjangan", "Tunjangan", 2],
  ["kasbon", "Kasbon", 2], ["potongan_lain", "Potongan", 2], ["gaji_bersih", "Total", 2],
];

export default function ReportPayroll() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [data, setData] = useState(null);
  const [allow, setAllow] = useState(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => { api.get("/payroll/periods").then((r) => setPeriods(r.data)); }, []);
  useEffect(() => {
    if (!periodId) return;
    api.get(`/reports/payroll-recap?period_id=${periodId}`).then((r) => setData(r.data));
    api.get(`/reports/allowance-recap?period_id=${periodId}`).then((r) => setAllow(r.data)).catch(() => setAllow(null));
  }, [periodId]);

  const rows = data?.details || [];
  const t = data?.totals || {};

  const exportCsv = () => toCSV("rekap_payroll.csv",
    COLS.map(([k, l]) => ({ label: l, value: (r) => r[k] })), rows);

  return (
    <div className="space-y-6">
      <div className="no-print space-y-4">
        <PageHeader title="Rekap Payroll" subtitle="Rekap gaji seluruh pegawai per periode">
          {rows.length > 0 && <>
            <Button variant="outline" onClick={() => setPreview(true)} data-testid="rekap-preview"><Eye className="h-4 w-4 mr-1" /> Pratinjau & Unduh</Button>
            <Button variant="outline" onClick={() => window.print()} data-testid="rekap-print"><Printer className="h-4 w-4 mr-1" /> Print</Button>
          </>}
        </PageHeader>
        <Card className="p-4 max-w-md">
          <F label="Pilih Periode">
            <Select value={periodId} onValueChange={setPeriodId}>
              <SelectTrigger data-testid="rekap-period"><SelectValue placeholder="Pilih periode" /></SelectTrigger>
              <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </Card>
      </div>

      {data && (
        <div className="print-area space-y-3">
          <h2 className="text-lg font-bold hidden print:block">Rekap Payroll — {data.period?.nama}</h2>
          <div className="overflow-x-auto scroll-thin rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="rekap-table">
              <thead><tr className="border-b bg-slate-100/80 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                {COLS.map(([k, l, a]) => <th key={k} className={`px-3 py-2.5 ${a ? "text-right" : "text-left"}`}>{l}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {COLS.map(([k, l, a]) => (
                      <td key={k} className={`px-3 py-2 ${a ? "text-right font-mono" : "font-medium text-slate-800"}`}>
                        {a === 2 ? rupiah(r[k]) : r[k]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="bg-slate-900 text-white font-mono font-bold">
                <td className="px-3 py-2.5 text-left" colSpan={3}>GRAND TOTAL</td>
                <td></td>
                <td className="px-3 py-2.5 text-right">{rupiah(t.gaji_pokok)}</td>
                <td className="px-3 py-2.5 text-right">{rupiah(t.uang_makan)}</td>
                <td className="px-3 py-2.5 text-right">{rupiah(t.uang_luar_kota)}</td>
                <td className="px-3 py-2.5 text-right">{rupiah(t.uang_lembur)}</td>
                <td className="px-3 py-2.5 text-right">{rupiah(t.total_tunjangan)}</td>
                <td className="px-3 py-2.5 text-right">{rupiah(t.kasbon)}</td>
                <td className="px-3 py-2.5 text-right">{rupiah(t.potongan_lain)}</td>
                <td className="px-3 py-2.5 text-right">{rupiah(t.gaji_bersih)}</td>
              </tr></tfoot>
            </table>
          </div>

          {allow?.rows?.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-heading font-bold text-slate-800">Rekap Tunjangan per Jenis</h3>
              <div className="overflow-x-auto scroll-thin rounded-xl border border-slate-200 bg-white max-w-xl" data-testid="allowance-recap">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-100/80 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2.5 text-left">Jenis Tunjangan</th><th className="px-3 py-2.5 text-right">Total</th>
                  </tr></thead>
                  <tbody>
                    {allow.rows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100" data-testid={`allowance-recap-row-${i}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">{r.jenis}</td>
                        <td className="px-3 py-2 text-right font-mono">{rupiah(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-slate-900 text-white font-mono font-bold">
                    <td className="px-3 py-2.5 text-left">TOTAL TUNJANGAN</td>
                    <td className="px-3 py-2.5 text-right">{rupiah(allow.total)}</td>
                  </tr></tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      <ExportPreview open={preview} onClose={() => setPreview(false)} title="Pratinjau Rekap Payroll"
        pdfUrl={periodId ? `/reports/payroll-recap/pdf?period_id=${periodId}` : null}
        onPrint={() => window.print()}
        items={[
          { label: "Unduh PDF", url: `/reports/payroll-recap/pdf?period_id=${periodId}`, filename: "rekap_payroll.pdf" },
          { label: "Unduh Excel", url: `/reports/payroll-recap/xlsx?period_id=${periodId}`, filename: "rekap_payroll.xlsx" },
          { label: "Unduh CSV", onClick: exportCsv },
        ]} />
    </div>
  );
}
